// A shaped ceiling in the UI: the room it is for, the Ceiling card, what its
// setters say, its marks on the plan, its direction wall picked on the plan
// (armed from the card), its fall arrow dragged onto a wall, and its grips.
// docs/claude/ceilings.md.
import {
  type CeilingShape,
  type ProductInstance,
  type Sheet,
  type Vec2,
} from '../../shared/types';
import {
  MULTI_KINDS,
  selectedCount,
  selectedOf,
} from '../selection';
import {
  apexFromCentre,
  CEILING_PICK_HINTS,
  CEILING_PICK_LABELS,
  CEILING_SHAPE_OPTIONS,
  type CeilingMarks,
  ceilingMarksShown,
  type CeilingNote,
  ceilingShapeOf,
  ceilingWarnings,
  DIRECTION_LABELS,
  EAVE_SHAPES,
  everyWallVertical,
  hasSetbacks,
  isFlatCeiling,
  resolveCeiling,
  reverseDirection,
  ridgeFromCentre,
  setApexAt,
  setApexFromCentre,
  setCeilingShape,
  setCeilingThickness,
  setDirectionWall,
  setEaveHeight,
  setPitch,
  setRidgeAt,
  setRidgeFromCentre,
  setRise,
  setSetback,
  setSetbackAt,
  setSpringingHeight,
  setWallSetback,
  shapeAvailability,
  springsInside,
  turnDirection90,
  uniformSetback,
  wallFacePitches,
} from '../ceiling';
import {
  armed,
  type ArmedPick,
  armPick,
  cancelArmed,
} from './armedPick';
import { pickableConnTagAt } from './connectionUi';
import {
  beginGesture,
  commitGesture,
  dropDrag,
  flashStatus,
  flashToolStatus,
  invalidate,
  layers,
  renderProperties,
  state,
  vp,
} from './core';
import { wallThicknessOf } from '../dent';
import {
  drawingDimStyle,
  formatDimension,
  formatDimensionDown,
} from '../dimFormat';
import {
  distToSegment,
  instanceToWorld,
  pointInPolygon,
  toInstanceLocal,
} from '../geometry';
import { type SelGrip } from './grips';
import { HANDLE_LABELS } from '../handles';
import {
  externalHeight,
  interiorHeight,
  ownCeilingThickness,
} from '../heights';
import { tr } from '../i18n';
import {
  buttonRow,
  infoRow,
  noteRow,
  numberRow,
  sectionTitle,
  selectRow,
} from './panelRows';
import {
  polyEdges,
  polyWallHit,
  wallView,
} from '../polyRoom';
import { ceilingMarksScreen } from '../render';
import { toPlan } from './view3d';
import {
  type BenchHover,
  canvas,
  editRoomTop,
  gestureSetbacks,
  objectStackAt,
  roomReachable,
  selectedInstance,
  setTool,
} from '../main';
import { activeSheet } from './sheetSpace';

/** This module's state that main.ts reads and writes too. */
export const ceilingUi = {
  // A fall arrow being dragged onto a wall: it lights the wall through
  // `benchAddHover` ('pick'), as Pick on plan (an armed pick) does.
  /** `sp`: where the press was; `moved`: the drag has gone 4 px from it — before
   *  that a release is a click, and a click on the arrow changes nothing. */
  ceilingArrowDrag: null as { instId: string; sp: Vec2; moved: boolean } | null,
  /** The last message a ceiling grip's drag flashed: each stop is said once per drag. */
  gripFlash: null as string | null,
};

/** The ONE "single selected room" of a shaped ceiling: the selected instance
 *  when it is the only object selected (one of its panes or doors still counts),
 *  with a shaped ceiling, outside its vertex edit. Its arrows drag, its grips show. */
export function ceilingRoom(): ProductInstance | null {
  if (selectedCount(state, MULTI_KINDS) !== 1) return null;
  const inst = selectedInstance();
  return inst && !isFlatCeiling(inst) && state.polyRoomVertexEditId !== inst.id ? inst : null;
}

/** The ceiling marks a room shows now, which its points and runs snap to: a
 *  selected room's, and every room's with Show ceilings on — on a sheet, by the
 *  sheet's own toggle. */
export function ceilingMarksNow(inst: ProductInstance, sheet?: Sheet): CeilingMarks | null {
  if (isFlatCeiling(inst)) return null;
  if (sheet) return ceilingMarksShown(inst, { all: sheet.showCeilings === true, selected: false, single: false });
  return ceilingMarksShown(inst, { all: state.showCeilings, selected: selectedOf(state, 'instance').includes(inst.id), single: ceilingRoom()?.id === inst.id });
}

/** The armed Pick on plan: a ceiling's direction wall, picked on the plan (armed
 *  from the Ceiling card: the next click on one of the room's walls sets it). */
interface CeilingPick extends ArmedPick {
  readonly kind: 'ceiling-wall';
  readonly instId: string;
}
function ceilingPick(): CeilingPick | null {
  const p = armed();
  return p?.kind === 'ceiling-wall' ? p as CeilingPick : null;
}

/** Room `id`, while it is the single selected room and of a shape with a direction wall. */
function directionRoom(id: string): ProductInstance | null {
  const room = ceilingRoom();
  return room?.id === id && DIRECTION_LABELS[ceilingShapeOf(room)] ? room : null;
}

/** The room a direction pick or a fall-arrow drag acts on: still the single
 *  selected room, of a shape with a direction wall, or the pick ends. */
export function ceilingPickRoom(): ProductInstance | null {
  const pick = ceilingPick();
  const id = pick?.instId ?? ceilingUi.ceilingArrowDrag?.instId;
  const room = id ? directionRoom(id) : null;
  if (!room && pick) cancelArmed();
  return room;
}

/** The wall of `room` a pick at `world` takes: inside the room its nearest wall
 *  (the heater-wall tool's reach), outside it only on a wall's outer face. */
export function ceilingPickWallAt(room: ProductInstance, world: Vec2): number | null {
  const local = toInstanceLocal(room, world);
  const inside = pointInPolygon(local, room.polygon!);
  return polyWallHit(wallView(room), local, inside ? 250 / vp.scale + 200 : 12 / vp.scale)?.edge ?? null;
}

/** A wall of `room` lit blue: the one a pick, or an arrow dropped, would take. */
export function ceilingWallHover(room: ProductInstance, w: number | null): BenchHover | null {
  const e = w != null ? polyEdges(wallView(room))[w] : undefined;
  return e ? { instId: room.id, polys: [[e.a, e.b, e.ib, e.ia]], kind: 'pick' } : null;
}

/** Pick on plan: the Select tool's sub-mode (another tool's armed hover would
 *  promise one thing and the click do another). Its click on one of the room's
 *  walls points the ceiling there (one undo step, none when it already did);
 *  anywhere else only ends it. It lasts while its room is the single selection
 *  and its shape still has a direction wall (a Shape edit to a truncated pyramid
 *  ends it). */
function armCeilingPick(inst: ProductInstance): void {
  toPlan();
  if (state.tool !== 'select') setTool('select');
  const pick: CeilingPick = {
    kind: 'ceiling-wall',
    instId: inst.id,
    cursor: 'crosshair',
    prompt: () => { const r = state.drawing.instances.find((i) => i.id === inst.id); return r ? tr(CEILING_PICK_HINTS[ceilingShapeOf(r)] ?? '') : ''; },
    click: (_e, sp) => {
      const room = directionRoom(inst.id);
      const w = room ? ceilingPickWallAt(room, vp.screenToWorld(sp)) : null;
      if (room && w != null) { beginGesture(); editRoomTop(room, () => setDirectionWall(room, w)); commitGesture(); }
    },
    lit: (world) => { const room = directionRoom(inst.id); return room ? ceilingWallHover(room, ceilingPickWallAt(room, world)) : null; },
    holds: () => directionRoom(inst.id) !== null,
    cancel: () => {},
  };
  armPick(pick);
}

/** Whether `sp` is on one of `room`'s DRAGGABLE fall arrows (a 6 px band along
 *  its 28 px shaft). */
function onCeilingArrow(room: ProductInstance, sp: Vec2): boolean {
  const sc = ceilingMarksScreen(vp, room, ceilingMarksShown(room, { all: state.showCeilings, selected: true, single: true }));
  return sc.arrows.some((a) => a.drag && distToSegment(sp, a.tail, a.tip) <= 3);
}

/** Whether a press here takes the single selected room's draggable fall arrow:
 *  only while nothing painted over it lies under the press (it is drawn in the
 *  room's top slot, so what stands in the room does not count, an object on a
 *  higher layer or a connection's tag does). A non-draggable arrow is an
 *  ordinary press. */
export function ceilingArrowAt(sp: Vec2): boolean {
  const room = state.tool === 'select' && !activeSheet() ? ceilingRoom() : null;
  if (!room || !onCeilingArrow(room, sp) || pickableConnTagAt(sp)) return false;
  return roomReachable(objectStackAt(vp.screenToWorld(sp), sp).seen, room.id, layers().roomTop.get(room.id));
}

/** The wall a fall arrow dragged to `sp` would point at: none until the drag has
 *  gone 4 px from its press (a click changes nothing), nor while it is back over
 *  the arrow (dropped where it was). */
export function ceilingArrowDropAt(sp: Vec2): number | null {
  const d = ceilingUi.ceilingArrowDrag, room = d ? ceilingPickRoom() : null;
  if (!d || !room) return null;
  if (Math.hypot(sp.x - d.sp.x, sp.y - d.sp.y) >= 4) d.moved = true;
  if (!d.moved || onCeilingArrow(room, sp)) return null;
  return ceilingPickWallAt(room, vp.screenToWorld(sp));
}

/** Esc during an arrow drag: dropped nowhere, nothing written or recorded. */
export function cancelCeilingArrowDrag(): void {
  ceilingUi.ceilingArrowDrag = null;
  dropDrag();
  if (state.benchAddHover?.kind === 'pick') state.benchAddHover = null;
  canvas.style.cursor = '';
  commitGesture();
  invalidate();
}

/** A ceiling grip's drag message, each once per drag (a held stop would repeat
 *  it on every mousemove). */
export function flashOnce(note: CeilingNote | null): void {
  const msg = ceilingNoteText(note);
  if (!msg || msg === ceilingUi.gripFlash) return;
  ceilingUi.gripFlash = msg;
  flashToolStatus(msg);
}
/** A new drag says its stops again. */
export function clearGripFlash(): void {
  ceilingUi.gripFlash = null;
}

/** The single selected room's ceiling grips: a pyramid's or a dome's apex (each
 *  coordinate snaps within 12 px to where the room puts it with none stored —
 *  the middle on a square or an oblong), a gable's ridge (snaps to the middle;
 *  Alt frees both) and a truncated pyramid's flat edges — each moves its own
 *  walls' setback, square to them, catching another wall's within 12 px. The
 *  apex and the flat edges can reach a wall, so they yield to the room's handles
 *  and features under them. */
export function ceilingGrips(room: ProductInstance): SelGrip[] {
  return ceilingMarksShown(room, { all: state.showCeilings, selected: true, single: true }).grips.flatMap((g): SelGrip[] => {
    const at = instanceToWorld(room, g.at);
    if (g.kind === 'setback' && g.walls?.length) {
      const wall = g.walls[0];
      return [{
        at, others: [], self: room.id, holds: room.id, apply: () => {}, yields: true,
        drag: (world, free) => editRoomTop(room, () => {
          flashOnce(setSetbackAt(room, wall, toInstanceLocal(room, world), free ? 0 : 12 / vp.scale));
          gestureSetbacks.delete(room.id); // a fit later in this gesture starts from the dragged setbacks
        }),
        figure: { label: HANDLE_LABELS.setback, set: (v) => editRoomTop(room, () => { const note = setWallSetback(room, wall, v); gestureSetbacks.delete(room.id); return note; }) },
      }];
    }
    if (g.kind === 'apex') {
      return [{
        at, others: [], self: room.id, holds: room.id, apply: () => {}, yields: true,
        drag: (world, free) => flashOnce(editRoomTop(room, () => setApexAt(room, toInstanceLocal(room, world), free ? 0 : 12 / vp.scale))),
      }];
    }
    if (g.kind === 'ridge') {
      return [{
        at, others: [], self: room.id, holds: room.id, apply: () => {},
        drag: (world, free) => editRoomTop(room, () => {
          const msg = setRidgeAt(room, toInstanceLocal(room, world));
          const off = ridgeFromCentre(resolveCeiling(room));
          if (!free && off != null && off !== 0 && Math.abs(off) * vp.scale <= 12) setRidgeFromCentre(room, 0);
          flashOnce(msg);
        }),
        figure: { label: HANDLE_LABELS.ridge, set: (v) => editRoomTop(room, () => setRidgeFromCentre(room, v)) },
      }];
    }
    return [];
  });
}

/** A room's [Ceiling] card. Every write is one gesture (nothing is recorded
 *  when the value did not change) and flashes why the setter clamped it. */
export function appendCeilingCard(body: HTMLElement, inst: ProductInstance): void {
  const edit = (write: () => CeilingNote | null): void => {
    beginGesture();
    const msg = ceilingNoteText(editRoomTop(inst, write));
    commitGesture();
    renderProperties();
    invalidate();
    if (msg) flashStatus(msg);
  };
  const dim = (mm: number): string => formatDimension(mm, drawingDimStyle(state.drawing));
  body.appendChild(sectionTitle('Ceiling'));
  const shape = ceilingShapeOf(inst);
  const disabled = new Map<string, string>();
  for (const [v] of CEILING_SHAPE_OPTIONS) {
    const why = v === shape ? null : shapeAvailability(inst, v);
    if (why) disabled.set(v, why);
  }
  body.appendChild(selectRow('Shape', CEILING_SHAPE_OPTIONS, shape, (v) => edit(() => setCeilingShape(inst, v as CeilingShape)), { disabled }));
  const cf = shape === 'flat' ? null : resolveCeiling(inst);
  if (cf) {
    const dir = DIRECTION_LABELS[shape];
    if (dir) {
      body.appendChild(infoRow(dir, `${tr('Wall')} ${(cf.wall ?? 0) + 1}`));
      const picking = (): boolean => ceilingPick()?.instId === inst.id;
      body.appendChild(buttonRow(picking() ? CEILING_PICK_LABELS.cancel : CEILING_PICK_LABELS.pick, () => (picking() ? cancelArmed() : armCeilingPick(inst))));
      if (shape !== 'barrel') body.appendChild(buttonRow('Reverse', () => edit(() => { reverseDirection(inst); return null; })));
      body.appendChild(buttonRow('Turn 90°', () => edit(() => { turnDirection90(inst); return null; })));
    }
    if (EAVE_SHAPES.includes(shape)) {
      // Every wall of a truncated pyramid vertical: no eave line, so no Eave
      // height or Rise to show or type (its stored eave waits for a sloped wall).
      if (!everyWallVertical(cf)) {
        body.appendChild(numberRow('Eave height (mm)', cf.eave, (v) => edit(() => setEaveHeight(inst, v)), { commit: true }));
        // Off the STORED crown, as Height shows it (a shape drawn flat keeps its own).
        body.appendChild(numberRow('Rise (mm)', externalHeight(inst) - cf.eave, (v) => edit(() => setRise(inst, v)), { commit: true }));
      }
      if (shape === 'slope' || shape === 'gable') body.appendChild(numberRow('Pitch (°)', cf.facePitchDeg[0] ?? 0, (v) => edit(() => setPitch(inst, v)), { commit: true }));
      // A pyramid or a truncated pyramid types its SHALLOWEST straight-wall face;
      // a rounded corner's facets and a vertical wall count in none.
      const faces = shape === 'frustum' || shape === 'pyramid' ? wallFacePitches(cf) : [];
      if (faces.length) {
        const lo = Math.min(...faces), hi = Math.max(...faces);
        body.appendChild(numberRow('Pitch (°)', lo, (v) => edit(() => setPitch(inst, v)), { commit: true }));
        if (hi - lo > 0.05) body.appendChild(infoRow('Steepest pitch (°)', String(Math.round(hi * 100) / 100)));
      }
      const fromCentre = ridgeFromCentre(cf);
      if (fromCentre != null) {
        const far = cf.facePitchDeg[1] ?? 0;
        if (Math.abs(far - (cf.facePitchDeg[0] ?? 0)) > 0.05) body.appendChild(infoRow('Far side pitch (°)', String(Math.round(far * 100) / 100)));
        body.appendChild(numberRow('Ridge from centre (mm)', fromCentre, (v) => edit(() => setRidgeFromCentre(inst, v)), { commit: true }));
      }
    }
    // A barrel / cove / dome stores its INSIDE springing; its rise is the arch's
    // above it, H − T − S off the STORED crown (a capped one's note gives the drawn
    // crown). A cove whose every wall stands up to its flat springs from none.
    if (springsInside(shape) && cf.springing != null && !everyWallVertical(cf)) {
      body.appendChild(numberRow('Springing height (mm)', cf.springing, (v) => edit(() => setSpringingHeight(inst, v)), { commit: true }));
      body.appendChild(numberRow('Inside rise (mm)', Math.max(0, externalHeight(inst) - cf.thickness - cf.springing), (v) => edit(() => setRise(inst, v)), { commit: true }));
    }
    if (hasSetbacks(shape)) {
      // Every wall's target; empty with '(mixed)' while the walls differ.
      const sb = uniformSetback(inst), mixed = !Number.isFinite(sb);
      body.appendChild(numberRow('Setback (mm)', mixed ? null : sb, (v) => edit(() => setSetback(inst, v)), { commit: true, ...(mixed ? { placeholder: tr('(mixed)') } : {}) }));
    }
    // The apex, from the middle of the room's outline in its own frame.
    const apex = apexFromCentre(cf);
    if (apex) {
      body.appendChild(numberRow('Apex X from centre (mm)', apex.x, (v) => edit(() => setApexFromCentre(inst, v, undefined)), { commit: true }));
      body.appendChild(numberRow('Apex Y from centre (mm)', apex.y, (v) => edit(() => setApexFromCentre(inst, undefined, v)), { commit: true }));
    }
  }
  body.appendChild(numberRow('Ceiling thickness (mm)', ownCeilingThickness(inst), (v) => edit(() => setCeilingThickness(inst, v)), {
    commit: true,
    placeholder: String(wallThicknessOf(inst)),
    onClear: () => edit(() => setCeilingThickness(inst, null)),
  }));
  if (cf) {
    // The eave each family does NOT store: the inside one, or a barrel's / dome's outside one.
    if (springsInside(shape)) body.appendChild(infoRow('Outside eave height', dim(cf.eave)));
    else body.appendChild(infoRow('Interior eave height', dim(cf.interiorEave)));
    body.appendChild(infoRow('Interior crown height', dim(cf.interiorCrown)));
  } else body.appendChild(infoRow('Interior height', dim(interiorHeight(inst))));
  for (const w of ceilingWarnings(inst)) body.appendChild(noteRow(ceilingNoteText(w)!, 'warn', true));
}

/** A ceiling setter's message as the command bar shows it: translated, its
 *  placeholders filled (a length in the drawing's dimension style, a figure
 *  under its `limit` rounded down). */
export function ceilingNoteText(m: CeilingNote | null): string | null {
  if (!m) return null;
  let s = tr(m.text);
  const style = drawingDimStyle(state.drawing);
  if (m.n != null) s = s.replace('{n}', (m.limit != null ? formatDimensionDown : formatDimension)(m.n, style));
  if (m.limit != null) s = s.replace('{limit}', formatDimension(m.limit, style));
  if (m.min != null) s = s.replace('{min}', String(m.min));
  if (m.max != null) s = s.replace('{max}', String(m.max));
  const then = ceilingNoteText(m.then ?? null);
  return then ? `${s} ${then}` : s;
}
